import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { GraduationCap, Linkedin, Loader2, UserPlus } from "lucide-react";

// Public "Alumni" landing page for an institute. Grid of all active alumni + CTA to register.
export default function AlumniPublicPage({
  slug,
  registerPath,
  heading = "Our Alumni",
  eyebrow = "Alumni Network",
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/public/website/${slug}`);
        setData(res.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) {
    return (
      <div className="py-24 text-center text-slate-500" data-testid="alumni-public-loading">
        <Loader2 className="animate-spin inline" /> Loading…
      </div>
    );
  }

  const alumni = (data?.alumni || []).filter(
    (a) => a && (a.status === "Active" || !a.status),
  );

  return (
    <div className="max-w-7xl mx-auto px-5 py-12" data-testid="alumni-public-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] tracking-[0.25em] uppercase text-slate-500 font-semibold">
            {eyebrow}
          </div>
          <h1 className="mt-2 font-serif text-3xl md:text-4xl text-slate-900">
            {heading}
          </h1>
          {data?.institute?.name ? (
            <p className="text-slate-500 text-sm mt-2">{data.institute.name}</p>
          ) : null}
        </div>
        <Link
          to={registerPath || "register"}
          data-testid="alumni-public-register-cta"
          className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-xs font-semibold tracking-wide uppercase px-5 py-2.5 rounded-sm"
        >
          <UserPlus size={14} /> Register as Alumni
        </Link>
      </div>

      {alumni.length === 0 ? (
        <div
          className="border border-dashed border-slate-200 rounded-md py-14 text-center text-slate-500"
          data-testid="alumni-public-empty"
        >
          <GraduationCap size={28} className="mx-auto mb-3" />
          No alumni profiles published yet. Be the first — use the Register button above.
        </div>
      ) : (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4"
          data-testid="alumni-public-grid"
        >
          {alumni.map((a) => (
            <div
              key={a.id || a.full_name}
              data-testid={`alumni-public-card-${a.id || a.full_name}`}
              className="bg-white border border-slate-200 rounded-sm overflow-hidden flex flex-col"
            >
              <div className="aspect-square relative overflow-hidden">
                {a.photo_url ? (
                  <img
                    src={a.photo_url}
                    alt={a.full_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-slate-100 text-slate-400 flex items-center justify-center">
                    <GraduationCap size={40} strokeWidth={1.2} />
                  </div>
                )}
                {a.batch_year ? (
                  <div className="absolute top-2 left-2 bg-black/65 text-white text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-semibold backdrop-blur-sm">
                    Batch {a.batch_year}
                  </div>
                ) : null}
                {a.linkedin_url ? (
                  <a
                    href={a.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute top-2 right-2 bg-[#0A66C2] hover:bg-[#084d92] text-white rounded-sm p-1.5 shadow-md"
                    aria-label={`${a.full_name} on LinkedIn`}
                  >
                    <Linkedin size={12} strokeWidth={2} />
                  </a>
                ) : null}
              </div>
              <div className="p-3 flex-1">
                <div className="text-slate-900 font-semibold text-sm leading-snug">
                  {a.full_name}
                </div>
                {a.current_profession ? (
                  <div className="text-slate-600 text-xs mt-1 line-clamp-3">
                    {a.current_profession}
                  </div>
                ) : null}
                {a.branch ? (
                  <div className="text-[10px] tracking-wide uppercase text-slate-400 mt-2">
                    {a.branch}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
